// T22.680 (Gordon 2026-10-28): the broken-media alert was only the "N broken"
// pill under the wheel. The hot bar tab that holds a broken item, the broken
// item itself in the hot bar, and its segment on the wheel must each show a
// (!) too. This tiny shared store carries the current wheel's broken rows from
// BrokenMediaIndicator (the one owner of the fetch) to the hot bar and to the
// D3 wheel engine (createPie), which is not React and so subscribes directly.
import { useSyncExternalStore } from "react";

// Rows that belong to one segment of the wheel (wallpaper / portrait rows
// have no segment and no hot bar item).
const SEGMENT_KINDS = ["segment_fill", "image_block", "media_block"];

// Hot bar tabs whose items are media files a broken row can point at.
export const HOTBAR_MEDIA_TABS = ["images", "videos", "audios", "documents"];

let state = { wheelId: null, items: [] };
const listeners = new Set();

export function publishBrokenMediaItems(wheelId, items) {
  const next = Array.isArray(items) ? items : [];
  if (
    String(state.wheelId) === String(wheelId) &&
    state.items.length === next.length &&
    state.items.every((row, i) => row === next[i])
  ) {
    return;
  }
  state = { wheelId: wheelId ?? null, items: next };
  listeners.forEach((l) => l());
}

export function getBrokenMediaItems() {
  return state;
}

export function subscribeBrokenMediaItems(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBrokenMediaItems() {
  return useSyncExternalStore(subscribeBrokenMediaItems, getBrokenMediaItems)
    .items;
}

// Segment ids (as strings) with broken media, only for the wheel the rows
// were fetched for -- any other wheel on the page gets none.
export function brokenSegmentIdsFor(snapshot, wheelId) {
  const ids = new Set();
  if (!snapshot || wheelId == null || snapshot.wheelId == null) return ids;
  if (String(snapshot.wheelId) !== String(wheelId)) return ids;
  (snapshot.items || []).forEach((row) => {
    if (row && SEGMENT_KINDS.includes(row.kind) && row.segment_id != null) {
      ids.add(String(row.segment_id));
    }
  });
  return ids;
}

// A hot bar item is broken when a broken row of the same segment names its
// file. Hot bar items keep the craft.js `src` (or `url`) the scanner reads.
export function isHotbarItemBroken(items, segmentId, item) {
  if (!Array.isArray(items) || !items.length || !item || segmentId == null) {
    return false;
  }
  const urls = [item.src, item.url].filter(Boolean);
  if (!urls.length) return false;
  return items.some(
    (row) =>
      row &&
      row.segment_id != null &&
      String(row.segment_id) === String(segmentId) &&
      urls.includes(row.url)
  );
}

// { images, videos, audios, documents } -> true when that tab holds at least
// one broken item.
export function brokenHotbarTabs(items, hotbarSegments) {
  const out = {};
  HOTBAR_MEDIA_TABS.forEach((tab) => {
    out[tab] = false;
  });
  if (!Array.isArray(items) || !items.length || !Array.isArray(hotbarSegments)) {
    return out;
  }
  hotbarSegments.forEach((segment) => {
    HOTBAR_MEDIA_TABS.forEach((tab) => {
      if (out[tab]) return;
      const list = segment?.items?.[tab];
      if (
        Array.isArray(list) &&
        list.some((it) => isHotbarItemBroken(items, segment?.segmentId, it))
      ) {
        out[tab] = true;
      }
    });
  });
  return out;
}
